import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { MessageCircle, Navigation, Instagram, Scissors } from "lucide-react";
import { RotatingBadge } from "./Logo";
import { easeOut } from "./motion";
import { waLink, MAPS_LINK, IG_LINK, IMG, IG_HANDLE } from "./data";

const MaskedLine = ({ children, index, className = "" }) => (
    <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
        <motion.span
            className={"block " + className}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, delay: 0.35 + index * 0.14, ease: easeOut }}
        >
            {children}
        </motion.span>
    </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay, ease: easeOut }}
    >
        {children}
    </motion.div>
);

export default function Hero() {
    const sectionRef = useRef(null);
    const tiltRef = useRef(null);
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 110, damping: 16 });
    const sry = useSpring(ry, { stiffness: 110, damping: 16 });

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

    const onTilt = (e) => {
        const r = tiltRef.current.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * 9);
        rx.set(-py * 9);
    };
    const resetTilt = () => {
        rx.set(0);
        ry.set(0);
    };

    return (
        <section
            id="top"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative overflow-hidden bg-maroon-900"
        >
            {/* gold hairline frame */}
            <div className="pointer-events-none absolute inset-3 z-20 hidden rounded-[2.2rem] border border-gold/15 sm:block" aria-hidden="true" />
            {/* depth glow */}
            <motion.div
                style={{ opacity: glowOpacity }}
                className="pointer-events-none absolute -right-40 top-1/4 -z-0 h-[42rem] w-[42rem] rounded-full bg-maroon-700/45 blur-[130px]"
                aria-hidden="true"
            />
            <motion.div
                style={{ opacity: glowOpacity }}
                className="pointer-events-none absolute -left-52 bottom-0 -z-0 h-[30rem] w-[30rem] rounded-full bg-gold/[0.07] blur-[110px]"
                aria-hidden="true"
            />

            <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-40">
                {/* left — type */}
                <div className="lg:col-span-7">
                    <FadeUp delay={0.15} className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <span className="font-hindi text-lg text-gold/75 sm:text-xl">बाबा गारमेंट्स</span>
                        <span className="hidden h-px w-10 bg-gold/40 sm:block" aria-hidden="true" />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-cream/60">
                            Kanpur • Complete Family Fashion Store
                        </span>
                    </FadeUp>

                    <h1
                        data-testid="hero-title"
                        className="mt-6 font-display text-[clamp(3.4rem,10.5vw,9.25rem)] font-black leading-[0.9] tracking-[-0.02em] text-cream"
                    >
                        <MaskedLine index={0}>BABA</MaskedLine>
                        <MaskedLine index={1} className="font-light italic tracking-normal text-gold text-glow-gold">
                            GARMENTS
                        </MaskedLine>
                    </h1>

                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1, delay: 0.9, ease: easeOut }}
                        className="mt-8 h-px w-40 origin-left bg-gold/60"
                        aria-hidden="true"
                    />

                    <FadeUp delay={1.0} className="mt-7 max-w-xl">
                        <p className="text-lg leading-relaxed text-cream/80 sm:text-xl">
                            <span className="font-semibold text-cream">Wholesale Specialists.</span>{" "}
                            Latest Trends &amp; Best Quality — for retailers &amp; bulk buyers across
                            the region.
                        </p>
                    </FadeUp>

                    <FadeUp delay={1.1} className="mt-6 flex flex-wrap gap-3">
                        <span
                            data-testid="hero-wholesale-badge"
                            className="rounded-full bg-gold px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-maroon-950"
                        >
                            Wholesale Only
                        </span>
                        <span className="rounded-full border border-gold/35 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                            Retailers &amp; Bulk Buyers
                        </span>
                        <span className="rounded-full border border-cream/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/70">
                            No Retail Rates — Enquire
                        </span>
                    </FadeUp>

                    <FadeUp delay={1.2} className="mt-9 flex flex-wrap items-center gap-4">
                        <a
                            href={waLink()}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-whatsapp-cta"
                            className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-[0.14em] text-maroon-950 shadow-[0_14px_44px_-10px_rgba(212,175,55,0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
                        >
                            <MessageCircle size={18} className="transition-transform duration-300 group-hover:-rotate-12" />
                            WhatsApp for Wholesale Rates
                        </a>
                        <a
                            href={MAPS_LINK}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-directions-cta"
                            className="inline-flex items-center gap-2.5 rounded-full border border-gold/40 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                        >
                            <Navigation size={17} />
                            Get Directions
                        </a>
                    </FadeUp>
                </div>

                {/* right — treated image */}
                <div className="lg:col-span-5">
                    <FadeUp delay={0.8} className="relative">
                        <RotatingBadge
                            text="WHOLESALE SPECIALISTS • KANPUR •"
                            icon={<Scissors size={22} />}
                            className="absolute -left-9 -top-9 z-20 hidden h-28 w-28 rounded-full border border-gold/30 bg-maroon-950/90 text-gold shadow-2xl md:block"
                        />
                        <div
                            ref={tiltRef}
                            onMouseMove={onTilt}
                            onMouseLeave={resetTilt}
                            data-testid="hero-image-card"
                            className="relative rounded-[2rem] border border-gold/30 bg-maroon-950/40 p-2 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.75)]"
                            style={{ perspective: 900 }}
                        >
                            <motion.div
                                style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
                                className="relative aspect-[3/4] max-h-[72vh] w-full overflow-hidden rounded-[1.6rem]"
                            >
                                <motion.img
                                    src={IMG.hero}
                                    alt="Rolls of colourful wholesale fabric at Baba Garments, Kanpur"
                                    style={{ y: imgY }}
                                    className="absolute inset-0 h-[115%] w-full object-cover"
                                    loading="eager"
                                />
                                {/* spotlight + editorial shade */}
                                <div
                                    className="pointer-events-none absolute inset-0"
                                    style={{
                                        background:
                                            "radial-gradient(120% 80% at 22% 12%, rgba(212,175,55,0.18) 0%, rgba(56,11,18,0) 46%), linear-gradient(180deg, rgba(38,6,11,0) 38%, rgba(38,6,11,0.88) 100%)",
                                    }}
                                    aria-hidden="true"
                                />
                                {/* instagram chip */}
                                <div className="absolute inset-x-4 bottom-4 flex items-center justify-between gap-3 rounded-2xl glass-dark border border-gold/25 px-4 py-3" data-testid="hero-instagram-chip">
                                    <span className="flex items-center gap-3">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold">
                                            <Instagram size={16} />
                                        </span>
                                        <span>
                                            <span className="block text-[10px] uppercase tracking-[0.24em] text-cream/55">
                                                Follow on Instagram
                                            </span>
                                            <a
                                                href={IG_LINK}
                                                target="_blank"
                                                rel="noreferrer"
                                                data-testid="hero-instagram-link"
                                                className="block text-sm font-semibold text-cream transition-colors duration-300 hover:text-gold"
                                            >
                                                {IG_HANDLE}
                                            </a>
                                        </span>
                                    </span>
                                    <span className="hidden font-hindi text-base text-gold/70 sm:block">थोक भाव</span>
                                </div>
                            </motion.div>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}